import { Failure, Success } from "@pdfking/errors"
import type { ArrayForm, Validator } from "@pdfking/forms"
import { AlmostNullQ } from "@pdfking/forms"
import type { ComponentFailure } from "@pdfking/imposition/internal"
import type { ComponentSchema, ImpositionSchema } from "@pdfking/imposition/internal"
import { ApplyComponentFailures } from "@pdfking/imposition/internal"
import type { Distance, Margins } from "@pdfking/math"
import { MM } from "@pdfking/quantity"

// python COVER_NORMAL (cover_grow.py), and the loose pages: DEFAULT_GROW =
// FOLD_NO and LOOSE_PAGE (fold_grow.py).
const COVER_NORMAL_ID = "c"
const LOOSE_PAGE_IDS = ["b", "bp"]

const BOOK_OPENING = MM(5) as Distance
const BOOK_REVERSE = MM(2.5) as Distance

/**
 * Mirrors Python `horizon_data_maker` / `universe_data_maker`
 * (`objects/hooks/library.py:172-183`): when a horizon/universe binding is
 * selected, each component's grow must follow the book-binding rules and its
 * padding defaults to the horizon values (2.5 all sides for inner and loose pages, 5x5x5x5 for
 * cover). Universe additionally forbids loose (single) pages.
 *
 * Only a padding left blank takes them: the parsed value can't tell, the field's
 * own default fills it with zero, so it is read off the raw input.
 *
 * Runs before the machine size is sized, so it reads each component's
 * `grow_kind` node: a blank `machine_size` still errors the array as a whole.
 */
export const ValidateHorizonStructure: Validator<ImpositionSchema> = (results, input) => {
  if (results.marking.error) return results

  const marking = results.marking.value ?? []
  const horizon = marking.some(p => p.id === "horizon")
  const universe = marking.some(p => p.id === "universe")
  if (!horizon && !universe) return results

  const failures: ComponentFailure[] = []
  const fail = (ci: number, key: string) =>
    failures.push({
      path: [ci, "grow_kind"],
      error: Failure(key, { input: undefined }),
    })

  const components = (results.component_array as ArrayForm<ComponentSchema>).results
  const given = (input as { component_array?: { padding?: unknown }[] } | undefined)
    ?.component_array
  components.forEach((component, ci) => {
    const grow_kind = component.results.grow_kind.value
    if (!grow_kind) return
    const { is_cover, is_book, id, sides_tuple } = grow_kind
    if (ci === 0 && is_cover) {
      if (id !== COVER_NORMAL_ID) fail(ci, "HORIZON_COVER_NO_FLAPS")
    } else if (is_cover) {
      fail(ci, "HORIZON_SINGLE_COVER_AT_START")
    } else if (is_book) {
      if ((sides_tuple ?? []).some(side => side > 4)) {
        fail(ci, "HORIZON_ONLY_QUARTINI")
      }
    } else if (!LOOSE_PAGE_IDS.includes(id)) {
      fail(ci, "HORIZON_ONLY_QUARTINI")
    } else if (universe) {
      fail(ci, "HORIZON_NO_LOOSE_PAGES")
    }

    if (!AlmostNullQ(given?.[ci]?.padding)) return

    const side = is_cover ? BOOK_OPENING : BOOK_REVERSE
    const padding: Margins<Distance> = { left: side, right: side, top: side, bottom: side }
    failures.push({ path: [ci, "padding"], error: Success(padding) })
  })

  return ApplyComponentFailures(results, failures)
}
